// src/modules/app-views/_apps/appmulti/services/pricingService.js
// *******************************************************************************
// APPMULTI PRICING SERVICE (pure engine — ported verbatim from legacy
// jarvisly-front-multi-v1 appmultiService.calculatePlanValues)
// *******************************************************************************
// Math primitives are ported LOCALLY (legacy `.toFixed` semantics) — NOT taken
// from v5 src/services/financialService.js, whose shims use Math.round and would
// diverge by pennies on half-way / float-edge values. Parity is the contract.
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { returnOnlyNumbers } from 'utils/helpers';

// *******************************************************************************
// MATH PRIMITIVES (verbatim legacy financialService — toFixed-based)
// *******************************************************************************
const fix = (value = 0, f = 0) => {
  if (isNaN(value)) return 0;
  const v = Number(value);
  if (!v) return 0;
  return f ? Number(v.toFixed(f)) : v;
};

const markup = (value = 0, m = 0, f = 0) => {
  if (isNaN(value) || isNaN(m)) return 0;
  const v = Number(value);
  if (!v) return 0;
  if (!m) return v;
  const result = v * (1 + m / 100);
  return f ? Number(result.toFixed(f)) : result;
};

const markdownReverse = (cost = 0, md = 0, f = 0) => {
  if (isNaN(cost) || isNaN(md)) return 0;
  const v = Number(cost);
  if (!v) return 0;
  if (!md) return v;
  const result = v / ((100 - md) / 100);
  return f ? Number(result.toFixed(f)) : result;
};

const discount = (value = 0, p = 0, f = 0) => {
  if (isNaN(value) || isNaN(p)) return 0;
  const v = Number(value);
  if (!v) return 0;
  if (!p) return v;
  const result = v * (1 - p / 100);
  return f ? Number(result.toFixed(f)) : result;
};

const percentageToValue = (value = 0, p = 0, f = 2) => {
  const v = Number(value);
  const pc = Number(p);
  if (isNaN(v) || isNaN(pc)) return 0;
  if (!v) return 0;
  if (!pc) return 0;
  const result = (pc / 100) * v;
  return f ? Number(result.toFixed(f)) : result;
};

// *******************************************************************************
// GRIEVANCE LEVELS (verbatim legacy appmultiService.buildLevels / getTravelGrievanceLevel)
// *******************************************************************************
const buildLevels = plan => {
  let enabledLevels;

  switch (plan?.grievanceLevel) {
    case 'level1':
      enabledLevels = 1;
      break;
    case 'level2':
      enabledLevels = 2;
      break;
    case 'level3':
      enabledLevels = 3;
      break;
    default:
      enabledLevels = 0;
      break;
  }

  const par = {};

  if (enabledLevels > 0 && plan?.grievanceMinAge1) {
    par.grievance1Ages = [
      plan.grievanceMinAge1,
      plan?.grievanceMaxAge1 || par.maxAge,
    ];
  } else {
    par.grievance1Ages = null;
  }

  if (enabledLevels > 1 && plan?.grievanceMinAge2) {
    par.grievance2Ages = [
      plan.grievanceMinAge2,
      plan?.grievanceMaxAge2 || par.maxAge,
    ];
  } else {
    par.grievance2Ages = null;
  }

  if (enabledLevels > 2 && plan?.grievanceMinAge3) {
    par.grievance3Ages = [
      plan.grievanceMinAge3,
      plan?.grievanceMaxAge3 || par.maxAge,
    ];
  } else {
    par.grievance3Ages = null;
  }

  return {
    level: enabledLevels,
    ...par,
  };
};

const getTravelGrievanceLevel = (plan, age) => {
  const gLevels = buildLevels(plan);

  if (gLevels.level > 0) {
    if (
      gLevels.grievance3Ages &&
      age >= gLevels.grievance3Ages[0] &&
      age <= gLevels.grievance3Ages[1]
    ) {
      return 3;
    }
    if (
      gLevels.grievance2Ages &&
      age >= gLevels.grievance2Ages[0] &&
      age <= gLevels.grievance2Ages[1]
    ) {
      return 2;
    }
    if (
      gLevels.grievance1Ages &&
      age >= gLevels.grievance1Ages[0] &&
      age <= gLevels.grievance1Ages[1]
    ) {
      return 1;
    }
  }

  return 0;
};

// *******************************************************************************
// CORE ENGINE (verbatim legacy appmultiService.calculatePlanValues)
// *******************************************************************************
const calculatePlanValues = ({
  plan,
  days,
  passengersArr,
  adminDiscount = plan?.summary?.total?.adminDiscount || {
    accountId: null,
    type: 'value',
    percentage: 0,
    value: 0,
  },
  adminAdjustment = plan?.summary?.total?.adminAdjustment || {
    accountId: null,
    type: 'value',
    percentage: 0,
    value: 0,
  },
  paymentMethod,
  creditCardInstallment,
}) => {
  const gLevels = buildLevels(plan);

  const _p = {
    currency: plan?.currency || 'brl',
    dailyPrice: plan?.dailyPrice || 0,
    price: plan?.price || 0,
    priceType: plan?.priceType || 'daily',
    days: days < plan?.minDays ? plan?.minDays : days,
    exchangeRate: plan?.__operator?.parameters?.__exchangeRate || 1,
    grievance1: plan?.grievanceAdditionAge1 || 0,
    grievance2: plan?.grievanceAdditionAge2 || 0,
    grievance3: plan?.grievanceAdditionAge3 || 0,
    markdown: plan?.markdown || 0,
    discount: plan?.discount || 0,
    minDays: plan?.minDays || 1,
    maxDays: plan?.maxDays || 365,
    minAge: plan?.minAge || 0,
    maxAge: plan?.maxAge || 100,
    grievanceLevel: gLevels.level,
    grievance1Ages: gLevels?.grievance1Ages,
    grievance2Ages: gLevels?.grievance2Ages,
    grievance3Ages: gLevels?.grievance3Ages,
  };

  // PROVIDER DEFAULT
  const pdP = ['package', 'table'].includes(_p.priceType)
    ? _p.price || _p.dailyPrice
    : (_p.price || _p.dailyPrice) * _p.days;
  const pdUSD = fix(pdP, 2);
  const pdTotalBRL = fix(pdUSD * _p.exchangeRate, 2);
  const pdDailyBRL = fix(pdTotalBRL / _p.days, 2);
  const pdNetTotalBRL = discount(pdTotalBRL, _p.discount, 2);
  const pdNetDailyBRL = fix(pdNetTotalBRL / _p.days, 2);
  const pdPrice = {
    dailyPrice: pdDailyBRL,
    totalPrice: pdTotalBRL,
    netDailyPrice: pdNetDailyBRL,
    netTotalPrice: pdNetTotalBRL,
    qty: 0,
    fullDailyPrice: 0,
    fullTotalPrice: 0,
    fullNetDailyPrice: 0,
    fullNetTotalPrice: 0,
  };

  // PROVIDER GRIEVANCE 1
  const pg1USD = markup(pdUSD, _p.grievance1, 2);
  const pg1BRL = fix(pg1USD * _p.exchangeRate, 2);
  const pg1DailyBRL = fix(pg1BRL / _p.days, 2);
  const pg1NetBRL = discount(pg1BRL, _p.discount, 2);
  const pg1NetDailyBRL = fix(pg1NetBRL / _p.days, 2);
  const pg1Price =
    _p.grievanceLevel > 0
      ? {
          dailyPrice: pg1DailyBRL,
          totalPrice: pg1BRL,
          netDailyPrice: pg1NetDailyBRL,
          netTotalPrice: pg1NetBRL,
          qty: 0,
          fullDailyPrice: 0,
          fullTotalPrice: 0,
          fullNetDailyPrice: 0,
          fullNetTotalPrice: 0,
        }
      : null;

  // PROVIDER GRIEVANCE 2
  const pg2USD = markup(pdUSD, _p.grievance2, 2);
  const pg2BRL = fix(pg2USD * _p.exchangeRate, 2);
  const pg2DailyBRL = fix(pg2BRL / _p.days, 2);
  const pg2NetBRL = discount(pg2BRL, _p.discount, 2);
  const pg2NetDailyBRL = fix(pg2NetBRL / _p.days, 2);
  const pg2Price =
    _p.grievanceLevel > 1
      ? {
          dailyPrice: pg2DailyBRL,
          totalPrice: pg2BRL,
          netDailyPrice: pg2NetDailyBRL,
          netTotalPrice: pg2NetBRL,
          qty: 0,
          fullDailyPrice: 0,
          fullTotalPrice: 0,
          fullNetDailyPrice: 0,
          fullNetTotalPrice: 0,
        }
      : null;

  // PROVIDER GRIEVANCE 3
  const pg3USD = markup(pdUSD, _p.grievance3, 2);
  const pg3BRL = fix(pg3USD * _p.exchangeRate, 2);
  const pg3DailyBRL = fix(pg3BRL / _p.days, 2);
  const pg3NetBRL = discount(pg3BRL, _p.discount, 2);
  const pg3NetDailyBRL = fix(pg3NetBRL / _p.days, 2);
  const pg3Price =
    _p.grievanceLevel > 2
      ? {
          dailyPrice: pg3DailyBRL,
          totalPrice: pg3BRL,
          netDailyPrice: pg3NetDailyBRL,
          netTotalPrice: pg3NetBRL,
          qty: 0,
          fullDailyPrice: 0,
          fullTotalPrice: 0,
          fullNetDailyPrice: 0,
          fullNetTotalPrice: 0,
        }
      : null;

  // RESELLER DEFAULT
  const rdTotalBRL = markdownReverse(pdTotalBRL, _p.markdown, 2);
  const rdDailyBRL = fix(rdTotalBRL / _p.days, 2);
  const rdNetTotalBRL = discount(rdTotalBRL, _p.discount, 2);
  const rdNetDailyBRL = fix(rdNetTotalBRL / _p.days, 2);
  const rdPrice = {
    dailyPrice: rdDailyBRL,
    totalPrice: rdTotalBRL,
    netDailyPrice: rdNetDailyBRL,
    netTotalPrice: rdNetTotalBRL,
    qty: 0,
    fullDailyPrice: 0,
    fullTotalPrice: 0,
    fullNetDailyPrice: 0,
    fullNetTotalPrice: 0,
  };

  // RESELLER GRIEVANCE 1
  const rg1BRL = markdownReverse(pg1BRL, _p.markdown, 2);
  const rg1DailyBRL = fix(rg1BRL / _p.days, 2);
  const rg1NetBRL = discount(rg1BRL, _p.discount, 2);
  const rg1NetDailyBRL = fix(rg1NetBRL / _p.days, 2);
  const rg1Price =
    _p.grievanceLevel > 0
      ? {
          dailyPrice: rg1DailyBRL,
          totalPrice: rg1BRL,
          netDailyPrice: rg1NetDailyBRL,
          netTotalPrice: rg1NetBRL,
          qty: 0,
          fullDailyPrice: 0,
          fullTotalPrice: 0,
          fullNetDailyPrice: 0,
          fullNetTotalPrice: 0,
        }
      : null;

  // RESELLER GRIEVANCE 2
  const rg2BRL = markdownReverse(pg2BRL, _p.markdown, 2);
  const rg2DailyBRL = fix(rg2BRL / _p.days, 2);
  const rg2NetBRL = discount(rg2BRL, _p.discount, 2);
  const rg2NetDailyBRL = fix(rg2NetBRL / _p.days, 2);
  const rg2Price =
    _p.grievanceLevel > 1
      ? {
          dailyPrice: rg2DailyBRL,
          totalPrice: rg2BRL,
          netDailyPrice: rg2NetDailyBRL,
          netTotalPrice: rg2NetBRL,
          qty: 0,
          fullDailyPrice: 0,
          fullTotalPrice: 0,
          fullNetDailyPrice: 0,
          fullNetTotalPrice: 0,
        }
      : null;

  // RESELLER GRIEVANCE 3 (legacy uses fix=3 here — quirk preserved)
  const rg3BRL = markdownReverse(pg3BRL, _p.markdown, 3);
  const rg3DailyBRL = fix(rg3BRL / _p.days, 2);
  const rg3NetBRL = discount(rg3BRL, _p.discount, 2);
  const rg3NetDailyBRL = fix(rg3NetBRL / _p.days, 2);
  const rg3Price =
    _p.grievanceLevel > 2
      ? {
          dailyPrice: rg3DailyBRL,
          totalPrice: rg3BRL,
          netDailyPrice: rg3NetDailyBRL,
          netTotalPrice: rg3NetBRL,
          qty: 0,
          fullDailyPrice: 0,
          fullTotalPrice: 0,
          fullNetDailyPrice: 0,
          fullNetTotalPrice: 0,
        }
      : null;

  // GRIEVANCE BY TRAVELLERS
  passengersArr.map(p => {
    const passengerAge = typeof p === 'object' ? p.age : p;
    let travelLevel = getTravelGrievanceLevel(plan, passengerAge);
    switch (travelLevel) {
      case 3:
        pg3Price.qty++;
        rg3Price.qty++;
        break;
      case 2:
        pg2Price.qty++;
        rg2Price.qty++;
        break;
      case 1:
        pg1Price.qty++;
        rg1Price.qty++;
        break;
      case 0:
      default:
        pdPrice.qty++;
        rdPrice.qty++;
        break;
    }
    return passengerAge;
  });

  const applyFull = c => {
    if (c?.qty) {
      const qty = c.qty;
      c.fullDailyPrice = fix(c.dailyPrice * qty, 2);
      c.fullTotalPrice = fix(c.totalPrice * qty, 2);
      c.fullNetDailyPrice = fix(c.netDailyPrice * qty, 2);
      c.fullNetTotalPrice = fix(c.netTotalPrice * qty, 2);
    }
  };
  [
    pdPrice,
    pg1Price,
    pg2Price,
    pg3Price,
    rdPrice,
    rg1Price,
    rg2Price,
    rg3Price,
  ].forEach(applyFull);

  const providerGrossPrice =
    (pdPrice?.fullTotalPrice || 0) +
    (pg1Price?.fullTotalPrice || 0) +
    (pg2Price?.fullTotalPrice || 0) +
    (pg3Price?.fullTotalPrice || 0);
  const providerNetPrice =
    (pdPrice?.fullNetTotalPrice || 0) +
    (pg1Price?.fullNetTotalPrice || 0) +
    (pg2Price?.fullNetTotalPrice || 0) +
    (pg3Price?.fullNetTotalPrice || 0);
  const resellerGrossPrice =
    (rdPrice?.fullTotalPrice || 0) +
    (rg1Price?.fullTotalPrice || 0) +
    (rg2Price?.fullTotalPrice || 0) +
    (rg3Price?.fullTotalPrice || 0);
  const resellerNetPrice =
    (rdPrice?.fullNetTotalPrice || 0) +
    (rg1Price?.fullNetTotalPrice || 0) +
    (rg2Price?.fullNetTotalPrice || 0) +
    (rg3Price?.fullNetTotalPrice || 0);

  const adjustedPrice =
    resellerNetPrice - (adminDiscount?.value || 0) + (adminAdjustment?.value || 0);

  const couponDiscount = {
    _id: null,
    type: 'percentage',
    discount: 0,
    discountValue: 0,
  };

  const totalWithCoupomPrice = adjustedPrice - (couponDiscount?.discountValue || 0);

  let paymentDiscount = 0;
  let paymentDiscountValue = 0;
  let paymentInstallmentValue = totalWithCoupomPrice;
  let paymentInstallment = creditCardInstallment
    ? parseInt(returnOnlyNumbers(creditCardInstallment))
    : 0;

  switch (paymentMethod) {
    case 'slip_pix':
    case 'pix':
      paymentDiscount = 5;
      paymentDiscountValue = percentageToValue(
        totalWithCoupomPrice,
        paymentDiscount,
        2,
      );
      break;
    case 'credit_card':
      paymentInstallmentValue = fix(totalWithCoupomPrice / paymentInstallment, 2);
      break;
    default:
      break;
  }

  const totalToPay = totalWithCoupomPrice - paymentDiscountValue;
  const resellerGrossProfit = resellerNetPrice - providerNetPrice;
  const resellerNetProfit = totalToPay - providerNetPrice;

  return {
    parameters: _p,
    providerDefaultPrice: pdPrice,
    providerGrievance1Price: pg1Price,
    providerGrievance2Price: pg2Price,
    providerGrievance3Price: pg3Price,
    resellerDefaultPrice: rdPrice,
    resellerGrievance1Price: rg1Price,
    resellerGrievance2Price: rg2Price,
    resellerGrievance3Price: rg3Price,
    total: {
      providerGrossPrice: fix(providerGrossPrice, 2),
      providerNetPrice: fix(providerNetPrice, 2),
      resellerGrossPrice: fix(resellerGrossPrice, 2),
      resellerGrossProfit: fix(resellerGrossProfit, 2),
      resellerNetPrice: fix(resellerNetPrice, 2),
      adminDiscount,
      adminAdjustment,
      totalAdjusted: fix(adjustedPrice, 2),
      couponDiscount,
      netPriceWithCouponDiscount: fix(totalWithCoupomPrice, 2),
      paymentDiscount,
      paymentDiscountValue,
      totalWithPaymentDiscount: fix(totalToPay, 2),
      totalToPay: fix(totalToPay, 2),
      resellerNetProfit: fix(resellerNetProfit, 2),
      paymentMethod,
      paymentInstallmentTitle:
        paymentMethod === 'credit_card' ? creditCardInstallment : '1X',
      paymentInstallment,
      paymentInstallmentValue,
    },
  };
};

// *******************************************************************************
// SERVICE OBJECT + EXPORTS
// *******************************************************************************
const _math = { fix, markup, markdownReverse, discount, percentageToValue };

const pricingService = {
  _math,
  buildLevels,
  getTravelGrievanceLevel,
  calculatePlanValues,
};

export default pricingService;
export { calculatePlanValues, buildLevels, getTravelGrievanceLevel };
